import { useEffect, useState, SyntheticEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Container } from '@mui/material';

import SalesTrendsChart from '../../../components/Chart/SalesTrendsChart';
import TopSellingProductsChart from '../../../components/Chart/TopSellingProductsChart';
import BranchPerformanceChart from '../../../components/Chart/BranchPerformanceChart';
import CustomerDemographicsChart from '../../../components/Chart/CustomerDemographicsChart';
import CustomerSegmentationChart from '../../../components/Chart/CustomerSegmentationChart';
import TabPanel from '../../../components/TabPanel';
import PageTitleWrapper from '../../../components/PageTitleWrapper';
import PageTitle from '../../../components/PageTitle';
import { companyService } from '@/data/companyService';
import { branchService } from '@/data/branchService';
import { Company } from '@/types/Company.interface';
import { Branch } from '@/types/Branch.interface';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import DashboardFilters from '@/components/dashboard/DashboardFilters';
import DashboardTabs, {
  DashboardTabKey
} from '@/components/dashboard/DashboardTabs';
import SalesReportView from '@/components/salesReport/SalesReportView';
import TableReportsView from '@/components/tableReports/TableReportsView';
import StockAlertsCard from '@/components/dashboard/StockAlertsCard';
import { useFeature } from '@/contexts/FeatureContext';
import { FeatureKey } from '@/types/Licensing.interface';
import { tokenDecoder } from '@/utils/jwt';
import { isFeatureLocked } from '@/layouts/SidebarLayout/Sidebar/SidebarMenu/menuVisibility';
import { headerBranchId } from '@/pages/integrations/food-delivery/helpers';

function DashboardMain() {
  const { t } = useTranslation();
  const {
    isSuperAdmin,
    isCompanyAdmin,
    isBranchAdmin,
    isAdminView,
    company,
    currentBranch
  } = useUserViewMode();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<DashboardTabKey>('sales');
  const { hasFeature } = useFeature();
  const token = localStorage.getItem('access_token');
  const decodedToken = token ? tokenDecoder(token) : null;
  const tableReportsLocked = isFeatureLocked(
    decodedToken?.role ?? null,
    decodedToken?.branchId ?? null,
    hasFeature,
    FeatureKey.TABLE_MANAGEMENT
  );
  const tabs: DashboardTabKey[] = [
    'sales',
    'trends',
    'top',
    ...(!isBranchAdmin ? (['branches'] as const) : []),
    ...(!tableReportsLocked ? (['tables'] as const) : []),
    'demographics',
    'segmentation'
  ];
  // A tab that is no longer offered (e.g. "Masa" after switching to a
  // branch without tables) falls back to the report, for the tab bar and
  // the panel alike.
  const shownTab: DashboardTabKey = tabs.includes(activeTab)
    ? activeTab
    : 'sales';
  const panel = (tab: DashboardTabKey) => ({
    index: tabs.indexOf(tab),
    value: tabs.indexOf(shownTab)
  });
  const stockLocked = isFeatureLocked(
    decodedToken?.role ?? null,
    decodedToken?.branchId ?? null,
    hasFeature,
    FeatureKey.STOCK
  );
  const batchesLocked = isFeatureLocked(
    decodedToken?.role ?? null,
    decodedToken?.branchId ?? null,
    hasFeature,
    FeatureKey.BATCHES
  );
  // Masa raporları şube bazlı: filtrede seçilen şube, yoksa üstteki şube.
  const tableReportBranchId =
    selectedBranchId ?? headerBranchId(currentBranch, isAdminView);

  const getAnalyticsScopeLabel = () => {
    if (selectedBranchId) {
      // Şube yöneticisinde şube listesi çekilmez; adı mevcut şubeden gelir.
      const branchName =
        branches.find((b) => b.id === selectedBranchId)?.name ??
        currentBranch?.name;
      return t('dashboard.branch.analytics', { branch: branchName });
    } else if (selectedCompanyId) {
      const companyName = companies.find(
        (c) => c.id === selectedCompanyId
      )?.name;
      return t('dashboard.company.analytics', { company: companyName });
    } else {
      return t('dashboard.overall.analytics');
    }
  };

  const fetchCompanies = async () => {
    if (!isSuperAdmin) return;

    try {
      const result = await companyService.getAllFlat({ getAll: true });
      setCompanies(result || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchBranches = async () => {
    if (isBranchAdmin) return;

    try {
      const result = await branchService.getAllFlat({
        getAll: true,
        ...(selectedCompanyId || (!isSuperAdmin && company?.id)
          ? { companyId: selectedCompanyId || company?.id }
          : {})
      });
      setBranches(result || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleCompanyChange = (companyId: number) => {
    setSelectedCompanyId(companyId);
    setSelectedBranchId(null);
  };

  const handleBranchChange = (branchId: number) => {
    setSelectedBranchId(branchId);
  };

  const handleResetFilters = () => {
    setSelectedCompanyId(null);
    setSelectedBranchId(null);
  };

  const handleTabChange = (
    _event: SyntheticEvent,
    newValue: DashboardTabKey
  ) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (isCompanyAdmin && company) {
      setSelectedCompanyId(company.id);
    }

    if (isBranchAdmin && currentBranch) {
      setSelectedBranchId(currentBranch.id);
    }

    if (isSuperAdmin) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, isCompanyAdmin, isBranchAdmin, company, currentBranch]);

  useEffect(() => {
    if ((isSuperAdmin && selectedCompanyId) || isCompanyAdmin) {
      fetchBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, isCompanyAdmin, isSuperAdmin]);

  return (
    <>
      <Helmet>
        <title>{t('dashboard.main.title')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageTitle
          heading={t('dashboard.welcome')}
          subHeading={t('dashboard.overview')}
        />
      </PageTitleWrapper>
      <Container
        disableGutters
        maxWidth={false}
        sx={{ maxWidth: '90%' }}
        className="dashboard-overview"
      >
        {(isSuperAdmin || (isCompanyAdmin && isAdminView)) && (
          <DashboardFilters
            isSuperAdmin={isSuperAdmin}
            isCompanyAdmin={isCompanyAdmin}
            companies={companies}
            branches={branches}
            selectedCompanyId={selectedCompanyId}
            selectedBranchId={selectedBranchId}
            onCompanyChange={handleCompanyChange}
            onBranchChange={handleBranchChange}
            onResetFilters={handleResetFilters}
          />
        )}

        <Box
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4" color="text.secondary">
            {getAnalyticsScopeLabel()}
          </Typography>
        </Box>

        <StockAlertsCard
          branchId={tableReportBranchId}
          showLowStock={!stockLocked}
          showExpiry={!batchesLocked}
        />

        <DashboardTabs
          tabs={tabs}
          activeTab={shownTab}
          onTabChange={handleTabChange}
        />

        {/* Satış Raporu: web POS "Raporlar" ile birebir aynı rapor
            (GET /reports/sales-summary). Eski Overview kartları (KPI, türe
            göre satış, gelir kaynakları, zayi) bu görünümün içinde. */}
        <TabPanel {...panel('sales')} sx={{ px: 2 }}>
          <SalesReportView
            branchId={selectedBranchId}
            companyId={selectedCompanyId}
            ready={!isBranchAdmin || Boolean(selectedBranchId)}
          />
        </TabPanel>

        <TabPanel {...panel('trends')}>
          <SalesTrendsChart
            companyId={selectedCompanyId}
            branchId={selectedBranchId}
          />
        </TabPanel>

        <TabPanel {...panel('top')}>
          <TopSellingProductsChart
            companyId={selectedCompanyId}
            branchId={selectedBranchId}
          />
        </TabPanel>

        {tabs.includes('branches') && (
          <TabPanel {...panel('branches')}>
            <BranchPerformanceChart companyId={selectedCompanyId} />
          </TabPanel>
        )}

        {tabs.includes('tables') && (
          <TabPanel {...panel('tables')} sx={{ px: 2 }}>
            <TableReportsView branchId={tableReportBranchId} />
          </TabPanel>
        )}

        <TabPanel {...panel('demographics')}>
          <CustomerDemographicsChart
            companyId={selectedCompanyId}
            branchId={selectedBranchId}
          />
        </TabPanel>

        <TabPanel {...panel('segmentation')}>
          <CustomerSegmentationChart
            companyId={selectedCompanyId}
            branchId={selectedBranchId}
          />
        </TabPanel>
      </Container>
    </>
  );
}

export default DashboardMain;
